import React from 'react';
import { XIcon, HeartIcon, StarIcon, ShareNetworkIcon, GlobeIcon, LockIcon, DownloadIcon, CopyIcon, MagicWandIcon, InfoIcon } from '@phosphor-icons/react';
import { Generation, User } from '../types';

interface ImageDetailModalProps {
    generation: Generation;
    user: User | null;
    isOpen: boolean;
    onClose: () => void;
    onToggleLike: (e: React.MouseEvent) => void;
    onToggleFavorite: (e: React.MouseEvent) => void;
    onTogglePublic: (e: React.MouseEvent) => void;
    isLiked: boolean;
    isFavorited?: boolean;
}

export const ImageDetailModal: React.FC<ImageDetailModalProps> = ({
    generation,
    user,
    isOpen,
    onClose,
    onToggleLike,
    onToggleFavorite,
    onTogglePublic,
    isLiked,
    isFavorited = false
}) => {
    if (!isOpen) return null;

    const copyPrompt = () => {
        navigator.clipboard.writeText(generation.prompt);
        // Toast logic would be ideal here
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-8 bg-carbon/90 backdrop-blur-md animate-in fade-in duration-200" onClick={onClose}>
            <div className="w-full max-w-6xl h-[90vh] bg-onyx border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row" onClick={e => e.stopPropagation()}>

                {/* Image Section */}
                <div className="flex-1 bg-carbon/50 relative group flex items-center justify-center overflow-hidden">
                    <img
                        src={generation.img}
                        alt={generation.prompt}
                        className="w-full h-full object-contain max-h-[90vh]"
                    />
                    <div className="absolute top-4 left-4 p-2 bg-carbon/60 backdrop-blur-md rounded-lg text-white opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="text-xs font-mono">{generation.width || 1024} x {generation.height || 1024}</span>
                    </div>
                </div>

                {/* Details Section */}
                <div className="w-full md:w-[400px] bg-onyx border-l border-white/5 flex flex-col h-full">
                    {/* Header */}
                    <div className="p-6 border-b border-white/5 flex items-start justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-white mb-1 line-clamp-1">{generation.title || 'Untitled Creation'}</h2>
                            <div className="flex items-center gap-2 text-xs text-zinc-500">
                                <span>{new Date(generation.created_at || Date.now()).toLocaleDateString()}</span>
                                <span>•</span>
                                <span>{generation.model || 'Standard Model'}</span>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2 text-zinc-400 hover:text-white transition-colors">
                            <XIcon className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content - Scrollable */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-8">
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-xs font-bold text-zinc-500 uppercase tracking-widest flex items-center gap-2">
                                    <MagicWandIcon className="w-3 h-3" /> Prompt
                                </label>
                                <button onClick={copyPrompt} className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1 transition-colors">
                                    <CopyIcon className="w-3 h-3" /> Copy
                                </button>
                            </div>
                            <p className="text-sm text-zinc-300 leading-relaxed bg-carbon/20 p-4 rounded-xl border border-white/5 font-mono">
                                {generation.prompt}
                            </p>
                        </div>

                        {generation.negative_prompt && (
                            <div>
                                <label className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2 block">
                                    Negative Prompt
                                </label>
                                <p className="text-xs text-zinc-400 bg-carbon/20 p-3 rounded-lg border border-white/5 font-mono">
                                    {generation.negative_prompt}
                                </p>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-3 rounded-lg bg-zinc-900/50 border border-white/5">
                                <label className="text-[10px] text-zinc-500 uppercase block mb-1">Guidance Scale</label>
                                <span className="text-sm text-white font-mono">{generation.settings?.guidance_scale || 7.5}</span>
                            </div>
                            <div className="p-3 rounded-lg bg-zinc-900/50 border border-white/5">
                                <label className="text-[10px] text-zinc-500 uppercase block mb-1">Steps</label>
                                <span className="text-sm text-white font-mono">{generation.settings?.num_inference_steps || 30}</span>
                            </div>
                            <div className="p-3 rounded-lg bg-zinc-900/50 border border-white/5">
                                <label className="text-[10px] text-zinc-500 uppercase block mb-1">Seed</label>
                                <span className="text-sm text-white font-mono">{generation.settings?.seed || 'Random'}</span>
                            </div>
                            <div className="p-3 rounded-lg bg-zinc-900/50 border border-white/5">
                                <label className="text-[10px] text-zinc-500 uppercase block mb-1">Sampler</label>
                                <span className="text-sm text-white font-mono">{generation.settings?.scheduler || 'Euler a'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Footer - Actions */}
                    <div className="p-6 border-t border-white/5 bg-zinc-900/30">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={onToggleLike}
                                    className={`p-3 rounded-xl flex items-center gap-2 transition-all ${isLiked ? 'bg-pink-500text-white' : 'bg-carbon/40 text-zinc-400 hover:bg-zinc-800'}`}
                                >
                                    <HeartIcon className={`w-5 h-5 ${isLiked ? 'fill-pink-500 text-pink-500' : ''}`} />
                                    <span className="text-sm font-medium">{generation.likes_count || 0}</span>
                                </button>
                                <button
                                    onClick={onToggleFavorite}
                                    className={`p-3 rounded-xl transition-all ${isFavorited ? 'bg-yellow-500/10 text-yellow-500' : 'bg-carbon/40 text-zinc-400 hover:bg-zinc-800'}`}
                                >
                                    <StarIcon className={`w-5 h-5 ${isFavorited ? 'fill-yellow-500' : ''}`} />
                                </button>
                            </div>

                            <div className="flex items-center gap-2">
                                {user?.id === generation.user_id && (
                                    <button
                                        onClick={onTogglePublic}
                                        className={`p-3 rounded-xl transition-all flex items-center gap-2 ${generation.is_public ? 'bg-indigo-500/10 text-indigo-400' : 'bg-carbon/40 text-zinc-400 hover:bg-zinc-800'}`}
                                        title={generation.is_public ? "Public" : "Private"}
                                    >
                                        {generation.is_public ? <GlobeIcon className="w-5 h-5" /> : <LockIcon className="w-5 h-5" />}
                                    </button>
                                )}
                                <button className="p-3 rounded-xl bg-indigo-600 text-white hover:bg-indigo-500 transition-colors shadow-lg shadow-indigo-500/20">
                                    <DownloadIcon className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
